import { Component, OnInit, Inject, OnDestroy, DestroyRef, inject } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { AuthenticationService, WebAppUser } from '@services/authentication.service';
import { CookieAccept_COOKIE, GlobalFunctions, ToS_COOKIE } from '@global/GlobalFunctions';
import { DOCUMENT } from '@angular/common';
import { environment } from '@environments/environment';
import { Subscription, firstValueFrom } from 'rxjs';
import { distinctUntilChanged, defaultIfEmpty, filter } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';
import { Account, IAccount } from '@services/accounts.service';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { ChangeDetectorRef } from '@angular/core';
import { UpdateService } from '@services/update-service.service';
import { ApplicationConfiguration, ApplicationService, ApplicationSettings, IConfigFilter } from '@services/application.service';
import { Router } from '@angular/router';
import { NotificationService, SystemMessageTypes } from '@services/notifications.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/**
 * Default Class
 */
@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {

	/** Collection of the Subscription Objects for this Component */
	private subscriptions: Map<string, Subscription> = new Map();

	/** Show the Spinner */
	public showSpinner: boolean = false
	/** Environment Variables */
	public env: any
	/** This Current User */
	public thisUser: WebAppUser
	/** Selected Account */
	public selectedAccount: IAccount
	/** Selected Account GUID */
	public selectedAccountGUID: any
	/** List of My Accounts */
	public myAccounts: IAccount[]
	/** Notifications for User Exist */
	public notificationsExist: boolean = false
	/** Observable Subject Finalise */
	private destroyRef = inject(DestroyRef);
	/** Cookies Accepted */
	public showCookieBanner: boolean;

	/** updateAvailable */
	public updateAvailable = false;

	/** maintenanceTimer */
	private maintenanceTimer: ReturnType<typeof setTimeout> | null = null;
	/** configTimer */
	private configTimer: ReturnType<typeof setTimeout> | null = null;

	/** Application Configuration */
	private appConfig: ApplicationConfiguration;

	/** isDestroyed */
	private isDestroyed = false;

	/**
	 * Default Constructor
	 * @param updateService 
	 * @param auth Auth Service
	 * @param authenticationService authentication Service
	 * @param siteSubscriptions site Subscriptions
	 * @param doc doc
	 * @param globalFunctions global Functions
	 * @param cookieService Cookie Service
	 * @param cdr ChangeDetectorRef
	 * @param applicationService: ApplicationService
	 * @param router 
	 * @param notificationService 
	 */
	constructor(
		private updateService: UpdateService,
		public auth: AuthService,
		private authenticationService: AuthenticationService,
		private siteSubscriptions: siteSubscriptions,
		@Inject(DOCUMENT) private doc: Document,
		private globalFunctions: GlobalFunctions,
		private cookieService: CookieService,
		private cdr: ChangeDetectorRef,
		private applicationService: ApplicationService,
		private router: Router,
		private notificationService: NotificationService
	) {
		/** Show th eApp Loading GIF */
		this.globalFunctions.displaySpinner(false)

		this.env = environment
	}

	/**
	 * Page On Initialise Event Handler
	 */
	ngOnInit(): void {

		try {
			this.auth.user$.pipe(
				takeUntilDestroyed(this.destroyRef), distinctUntilChanged(),
				filter((authUser) => !!authUser)
			).subscribe({
				next: async (authUser: any) => {

					if (!authUser.email_verified) {
						this.authenticationService.logout(this.doc, '/unverified');
						return;
					}
					//Update the logged on user
					if (authUser) { this.GetUser(authUser) }

					this.subscribeCurrentUser()

				},
				error: (error) => { this.globalFunctions.HandleError(error, "Error: this.auth.user$", false) },
			})

		}
		catch (error) { console.error(error) }

		this.globalFunctions.showSpinner$.pipe(
			takeUntilDestroyed(this.destroyRef)
		).subscribe((display: boolean) => {
			this.showSpinner = display
			this.cdr.detectChanges()
		})

		/** Show a banner for use of cookies */
		// this.cookieService.set('cookieAcknowledged', '');
		const cookieAcknowledged = this.cookieService.get(CookieAccept_COOKIE);
		if (!cookieAcknowledged && this.env.production) {
			// Display the banner or perform any other action
			this.showCookieBanner = true;
		}

	}

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {

			this.isDestroyed = true;

			// Unsubscribe from all subscriptions			
			this.unsubscribeFromAll()

			if (this.maintenanceTimer) {
				clearTimeout(this.maintenanceTimer);
				this.maintenanceTimer = null;
			}

			if (this.configTimer) {
				clearTimeout(this.configTimer);
				this.configTimer = null;
			}

		}
		catch { }
	}

	/**
	 * setupSubscriptions
	 */
	async setupSubscriptions() {
		await this.CheckAppConfig();
		this.subscribeSettings()
		this.CheckTermsofSercice()
		this.subscribeUpdates()
		this.subscribeAccounts()
		this.subscribeAccountGUID()
	}

	/**
	 * subscribeUpdates
	 */
	subscribeUpdates() {
		/** Subscribe to the App Update Service */
		this.updateService.updateAvailable$.pipe(
			takeUntilDestroyed(this.destroyRef)
		).subscribe(flag => {

			/** Check the site configuration */
			if (this.appConfig) {

				/** Check the deployment settings */
				if (this.appConfig.deployment?.silent && flag == true) {

					/** If silent deployment, update immediately */
					this.updateService.activateUpdate(false);

				} else {

					/** If not a silent deployment, show the update notification */
					this.updateAvailable = flag;
				}
			}
		});
	}

	/**
	 * subscribeSettings
	 */
	subscribeSettings() {
		this.siteSubscriptions.applicationSettings$.pipe(
			distinctUntilChanged(),
			filter((result) => !!result),
			takeUntilDestroyed(this.destroyRef)
		).subscribe((appSettings: ApplicationSettings) => {
			try {
				// this.appConfig = this.applicationService.mergeDefaultConfiguration(appSettings.config);
				this.appConfig = appSettings.Config
				this.checkMaintenance();
				this.setupMaintenanceTimer();
			}
			finally { }
		})
	}

	/**
	 * Update User Login Information
	 * @param profile
	 * @returns
	 */
	async GetUser(profile: any): Promise<void> {
		try {
			let id: string = profile["sub"];
			if (id != "") {
				try {
					const user = await firstValueFrom(
						this.authenticationService.getUser$(id)
					);
					if (user) { this.authenticationService.setCurrentUser(user); }
					else { this.authenticationService.CreateUser(profile); }
				}
				catch (error: any) {
					if (error.status == 404) {
						this.authenticationService.CreateUser(profile);
					} else {
						this.globalFunctions.HandleError(error, "Error: this.authenticationService.getUser$", false);
					}
				}
			}
		}
		catch (error) { console.error(`updateLogin:${this.GetUser.name}:`, error) }
	}

	/**
	 * Get a list of accounts for this logged on user
	 * @param userPublicID
	 */
	getAccounts(userPublicID: string): void {

		let showDisabled = false
		this.myAccounts = []

		let data = {
			"userPublicID": userPublicID,
			"showDisabled": showDisabled
		};

		this.siteSubscriptions.RefreshMyAccounts(data);
	}

	/**
	 * Get a list of accounts for this logged on user
	 */
	GetOrganisations(userPublicID: string, AccountGUID: string, OrganisationID: string = "") {
		this.siteSubscriptions.RefreshMyOrganisations(AccountGUID, OrganisationID);
	}

	/**
	 * subscribeCurrentUser
	 */
	subscribeCurrentUser() {
		this.authenticationService.currentUser$.pipe(
			takeUntilDestroyed(this.destroyRef), distinctUntilChanged(),
			filter((currentUser) => !!currentUser)
		).subscribe({
			next: (currentUser) => {
				this.thisUser = <WebAppUser>currentUser;
				if (this.thisUser.Disabled) {
					this.authenticationService.logout(this.doc, '/accountdisabled');
				}
				// Get the Accounts for this user
				this.getAccounts(this.thisUser.Sub);
				this.setupSubscriptions();
			},
			error: (error) => { this.globalFunctions.HandleError(error, "Error: this.authenticationService.currentUser$", false) },
		})
	}

	/**
	 * subscribeAccountGUID
	 */
	subscribeAccountGUID() {
		try {
			// Unsubscribe from existing subscription to this specific observable if it exists
			this.unsubscribeFromObservable("selectedAccountGUID$");
			// Subscribe to selectedAccountGUID
			const subscription = this.siteSubscriptions.selectedAccountGUID$.pipe(
				takeUntilDestroyed(this.destroyRef),
				distinctUntilChanged(),
				defaultIfEmpty(null), // emit null if no user is emitted
				// finalize(() => { })
			).subscribe({
				next: (accountGUID) => {
					if (accountGUID) {
						if (accountGUID != this.selectedAccountGUID) {
							this.selectedAccountGUID = accountGUID;
							if (this.myAccounts) {
								this.selectedAccount = <IAccount>this.myAccounts.find(({ GUID }) => GUID.toUpperCase() === accountGUID.toUpperCase())
								if (this.selectedAccount && this.thisUser) {
									this.GetOrganisations(this.thisUser.Sub, accountGUID);
								}
							}
						}
					} else {
						if (this.selectedAccountGUID) { this.GetOrganisations(this.thisUser.Sub, this.selectedAccountGUID) }
					}
				},
				error: (error) => { this.globalFunctions.HandleError(error, "Error: this.siteSubscriptions.selectedAccountGUID$", false) },
			});
			// Store the subscription in the array
			this.subscriptions.set('selectedAccountGUID$', subscription);
		}
		catch (error) { console.error(error) }
	}

	/**
	 * subscribeAccounts
	 */
	subscribeAccounts() {
		try {
			// Unsubscribe from existing subscription to this specific observable if it exists
			this.unsubscribeFromObservable("myAccounts$");
			// Subscribe to myAccounts
			const subscription = this.siteSubscriptions.myAccounts$.pipe(
				takeUntilDestroyed(this.destroyRef),
				distinctUntilChanged(),
				filter((myAccounts) => !!myAccounts)
			).subscribe({
				next: (myAccounts) => {
					this.myAccounts = myAccounts;
					this.myAccounts = this.siteSubscriptions.normalizeFeatures(this.myAccounts) as Account[];
				},
				error: (error) => { this.globalFunctions.HandleError(error, "Error: this.siteSubscriptions.myAccounts$", false) },
			});
			// Store the subscription in the array
			this.subscriptions.set('myAccounts$', subscription);
		}
		catch (error) { console.error(error) }
	}

	/**
	 * acknowledgeCookies
	 */
	acknowledgeCookies() {
		// Set the cookie to acknowledge
		this.cookieService.set(CookieAccept_COOKIE, 'true', {
			expires: 30,
			path: '/'
		});
		// Hide the banner
		this.showCookieBanner = false;
	}

	/**
	 * Method to unsubscribe from a specific observable by key
	 */
	private unsubscribeFromObservable(key: string) {
		try {
			const subscription = this.subscriptions.get(key);
			if (subscription) {
				subscription.unsubscribe();
				this.subscriptions.delete(key);
			}
		} catch (error) { }
	}

	/**
	 * Method to unsubscribe from all subscriptions
	 */
	unsubscribeFromAll() {
		try {
			this.subscriptions.forEach(subscription => subscription.unsubscribe());
			this.subscriptions.clear();
		} catch (error) { }
	}

	/** reload */
	reload() {
		this.updateService.activateUpdate();
	}

	/**
	 * CheckAppConfig
	 */
	async CheckAppConfig() {

		if (this.isDestroyed) { return; }

		var timeout = 30 * 60 * 1000;
		try {
			const cfgFilter = <IConfigFilter>{
				application: this.env.appName,
				environment: this.env.production ? 'Production' : "Development",
				development: !this.env.production
			}
			const result = await firstValueFrom(
				this.applicationService.getSettings$(cfgFilter)
			);

			if (this.isDestroyed) { return; }

			var appSettings = new ApplicationSettings(JSON.parse(result[0].Data));
			this.siteSubscriptions.setApplicationSettings(appSettings);
			var appConfiguration = appSettings.Config;

			if (Object.keys(appConfiguration).length > 0 &&
				appConfiguration.maintenance.enabled &&
				appConfiguration.maintenance.start != null) {

				/** Calculate the time until maintenance starts */
				const timeUntilMaintenance = appConfiguration.maintenance.start - Date.now();

				/** Poll more frequently during the hour before maintenance */
				if (timeUntilMaintenance > 0 &&
					timeUntilMaintenance <= 60 * 60 * 1000) {
					timeout = 5 * 60 * 1000;
				}

			}

		} finally {
			if (!this.isDestroyed) {

				if (this.configTimer) {
					clearTimeout(this.configTimer);
					this.configTimer = null;
				}

				this.configTimer = setTimeout(() => {
					console.log(`app.component: this.configTimer:${timeout}`)
					this.CheckAppConfig();
				}, timeout);
			}
		}
	}

	/**
	 * setupMaintenanceTimer
	 * @returns 
	 */
	private setupMaintenanceTimer(): void {

		try {

			if (this.isDestroyed) { return; }

			/** If timer exists */
			if (this.maintenanceTimer) {
				clearTimeout(this.maintenanceTimer);
				this.maintenanceTimer = null;
			}
			/** Read Config */
			const maintenance = this.appConfig.maintenance;

			/** Is maintenance enabled? */
			if (!maintenance?.enabled ||
				maintenance.start == null) {
				return;
			}

			/** Calculate delay to start */
			const delay = maintenance.start - Date.now();

			/** Is maintenance in porogress? */
			if (delay <= 0) {
				// Maintenance has already started
				this.checkMaintenance();
				return;
			}

			/** Schedule the next time to check */
			this.maintenanceTimer = setTimeout(() => {
				console.log("app.component: this.maintenanceTimer")
				this.maintenanceTimer = null;
				this.checkMaintenance();
			}, delay);
		}
		finally { }

	}

	/**
	 * checkMaintenance
	 * @returns 
	 */
	private checkMaintenance(): void {

		try {
			const maintenance = this.appConfig.maintenance;

			if (!maintenance?.enabled ||
				maintenance.start == null) {
				this.notificationService.cleanSystemNotifications(
					SystemMessageTypes.SiteMainenancePlanned
				);
				return;
			}

			/** Current time */
			const now = Date.now();

			/** Is maintenance currently active? */
			if (now >= maintenance.start
			) {

				this.notificationService.cleanSystemNotifications(
					SystemMessageTypes.SiteMainenancePlanned
				);

				this.router.navigateByUrl('/maintenance');

				return;
			}

			/** Maintenance is scheduled for the future */
			if (maintenance.start > now) {

				const start = new Date(maintenance.start);
				const end = maintenance.end != null
					? new Date(maintenance.end)
					: null;

				const msg = end
					? `Scheduled maintenance from ${start.toLocaleString('en-GB')} to ${end.toLocaleString('en-GB')}`
					: `Scheduled maintenance from ${start.toLocaleString('en-GB')}`;

				/** Allow a snooze for 6 hours */
				var snoozeMS = 6 * 60 * 60 * 1000;
				this.notificationService.putSystem("", SystemMessageTypes.SiteMainenancePlanned, msg, false, true, snoozeMS);
			}

		}
		finally { }
	}

	/** CheckTermsofSercice */
	private CheckTermsofSercice(): boolean {

		/** Check current route */
		const isOnToSPage = this.router.url === '/user-agreement';
		if (isOnToSPage) {
			return false;
		}

		const ToS = this.appConfig.ToS;

		if (!ToS || !ToS.check) {
			this.cookieService.delete(ToS_COOKIE, '/');
			return false
		}

		const cookieValue = this.cookieService.get(ToS_COOKIE);
		/** No cookie means the user has not accepted the ToS */
		if (!cookieValue) {
			if (!isOnToSPage) {
				this.router.navigateByUrl('/user-agreement');
			}
			return false;
		}

		const ToS_VERSION = ToS.version ?? 1.0;
		const acceptedVersion = parseFloat(cookieValue);

		/** Invalid or older versions require acceptance */
		if (isNaN(acceptedVersion) || acceptedVersion < ToS_VERSION) {
			if (!isOnToSPage) {
				this.router.navigateByUrl('/user-agreement');
				return false;
			}
		}

		return true;
	}

}